import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import SecureLS from "secure-ls";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { fetchStoreSettingsStatus } from "../services/storeSettingsService";

const ls = new SecureLS({ encodingType: "aes" });

const toIsoOrNull = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const StoreTitle = ({
  title,
  onTitleClick,
  onTitleDoubleClick,
  titleStyle,
  showOperationalStatus = true,
}) => {
  const user = ls.get("user");
  const envMode =
    (process.env.REACT_APP_APP_MODE || process.env.REACT_APP_API_MODE || "")
      .toUpperCase()
      .trim();
  const isLocalEnv = envMode.startsWith("LOCAL");

  const [mode, setMode] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [localStatus, setLocalStatus] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const mountedRef = useRef(true);
  const hasFetchedOnceRef = useRef(false);
  const statusSnapshotRef = useRef(null);

  const storeName =
    localStatus?.storeName ||
    user?.storeName ||
    process.env.REACT_APP_STORE_NAME ||
    "";
  const storeId = user?.posId || user?.storeId || process.env.REACT_APP_STORE_ID;

  const pushStatusSnackbar = useCallback((message, severity = "info") => {
    if (!mountedRef.current || !message) return;
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const notifyStatusChanges = useCallback(
    (nextSnapshot) => {
      const prev = statusSnapshotRef.current;
      if (!prev) return false;

      const changes = [];
      let severity = "info";

      if (prev.mode !== nextSnapshot.mode) {
        changes.push(`Mode changed to ${nextSnapshot.mode || "Unknown"}`);
      }

      if (prev.cloudReachable !== nextSnapshot.cloudReachable) {
        const cloudLabel =
          nextSnapshot.cloudReachable === true
            ? "Online"
            : nextSnapshot.cloudReachable === false
            ? "Offline"
            : "Unknown";
        changes.push(`Cloud is now ${cloudLabel}`);
        severity = nextSnapshot.cloudReachable === false ? "warning" : "success";
      }

      if (prev.outboxPending !== nextSnapshot.outboxPending) {
        const pending =
          typeof nextSnapshot.outboxPending === "number"
            ? nextSnapshot.outboxPending
            : "N/A";
        changes.push(`Outbox pending: ${pending}`);
      }

      if (prev.lastSync !== nextSnapshot.lastSync) {
        changes.push("Last sync updated");
      }

      if (changes.length) {
        pushStatusSnackbar(changes.join(" | "), severity);
        return true;
      }
      return false;
    },
    [pushStatusSnackbar]
  );

  const fetchStatus = useCallback(async ({ forceRefresh = false } = {}) => {
    try {
      if (!showOperationalStatus) return;
      if (mountedRef.current) setIsCheckingStatus(true);
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      if (!apiBase) return;

      if (isLocalEnv) {
        const { data } = await axios.get(`${apiBase}/v1/api/local/status`);
        if (!mountedRef.current) return;

        const nextSync =
          data?.lastOutboxSentAt ||
          data?.lastEventSuccessAt ||
          data?.lastEventSyncAt ||
          null;
        const nextSnapshot = {
          mode: "LOCAL_FIRST",
          cloudReachable:
            typeof data?.cloudReachable === "boolean" ? data.cloudReachable : null,
          outboxPending:
            typeof data?.outboxPending === "number" ? data.outboxPending : null,
          lastSync: toIsoOrNull(nextSync),
        };

        if (hasFetchedOnceRef.current) {
          const changed = notifyStatusChanges(nextSnapshot);
          if (!changed) pushStatusSnackbar("No status changes", "info");
        } else hasFetchedOnceRef.current = true;
        statusSnapshotRef.current = nextSnapshot;

        setLocalStatus(data || null);
        setMode("LOCAL_FIRST");
        setLastSync(nextSync);
        return;
      }

      if (!storeId) return;
      const data = await fetchStoreSettingsStatus({
        apiBase,
        storeId,
        forceRefresh,
      });
      if (!mountedRef.current) return;

      const modeVal = data?.settings?.mode || null;
      const syncAt =
        data?.settings?.lastSyncAt ||
        data?.syncStatus?.lastEventAppliedAt ||
        data?.syncStatus?.lastEventReceivedAt ||
        null;
      const nextSnapshot = {
        mode: modeVal,
        cloudReachable: null,
        outboxPending: null,
        lastSync: toIsoOrNull(syncAt),
      };

      if (hasFetchedOnceRef.current) {
        const changed = notifyStatusChanges(nextSnapshot);
        if (!changed) pushStatusSnackbar("No status changes", "info");
      } else hasFetchedOnceRef.current = true;
      statusSnapshotRef.current = nextSnapshot;

      setMode(modeVal);
      setLastSync(syncAt);
    } catch (error) {
      if (mountedRef.current) {
        setLocalStatus(null);
        setMode(null);
      }
    } finally {
      if (mountedRef.current) setIsCheckingStatus(false);
    }
  }, [
    isLocalEnv,
    notifyStatusChanges,
    pushStatusSnackbar,
    showOperationalStatus,
    storeId,
  ]);

  useEffect(() => {
    if (!showOperationalStatus) return undefined;
    mountedRef.current = true;
    fetchStatus();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchStatus, showOperationalStatus]);

  const renderMode = () => {
    if (!showOperationalStatus) return null;
    if (!mode) return null;
    const label =
      mode === "LOCAL_FIRST"
        ? "Local-first"
        : mode === "CLOUD_ONLY"
        ? "Cloud-only"
        : mode;
    const syncText = lastSync ? new Date(lastSync).toLocaleString() : "N/A";

    const handleRetryStatus = () => {
      if (isCheckingStatus) return;
      fetchStatus({ forceRefresh: true });
    };

    if (isLocalEnv) {
      const cloudReachable = localStatus?.cloudReachable;
      const outboxPending = localStatus?.outboxPending;
      const cloudText =
        cloudReachable === true
          ? "Online"
          : cloudReachable === false
          ? "Offline"
          : "Unknown";

      return (
        <p
          style={{
            fontSize: "0.95rem",
            color: "#444",
            margin: "0.25rem 0 0",
            cursor: isCheckingStatus ? "not-allowed" : "pointer",
          }}
          onClick={handleRetryStatus}
          title="Retry status"
        >
          Mode: <span style={{ fontWeight: 600 }}>{label}</span>
          <span style={{ marginLeft: "0.5rem", color: "#666" }}>
            Cloud: {cloudText}
          </span>
          <span style={{ marginLeft: "0.5rem", color: "#666" }}>
            Last sync: {syncText}
          </span>
          {typeof outboxPending === "number" && (
            <span style={{ marginLeft: "0.5rem", color: "#666" }}>
              Outbox pending: {outboxPending}
            </span>
          )}
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              handleRetryStatus();
            }}
            disabled={isCheckingStatus}
            title="Retry status"
            aria-label="Retry status"
            style={{
              marginLeft: "0.5rem",
              border: "none",
              background: "transparent",
              color: "#666",
              cursor: isCheckingStatus ? "not-allowed" : "pointer",
              fontSize: "0.95rem",
              lineHeight: 1,
              padding: 0,
              display: "inline-block",
              animation: isCheckingStatus
                ? "storeTitleSpin 1s linear infinite"
                : "none",
            }}
          >
            {"\u27F3"}
          </button>
        </p>
      );
    }

    if (mode !== "LOCAL_FIRST") return null;

    return (
      <p
        style={{
          fontSize: "0.95rem",
          color: "#444",
          margin: "0.25rem 0 0",
          cursor: isCheckingStatus ? "not-allowed" : "pointer",
        }}
        onClick={handleRetryStatus}
        title="Retry status"
      >
        Mode: <span style={{ fontWeight: 600 }}>{label}</span>
        <span style={{ marginLeft: "0.5rem", color: "#666" }}>
          Last sync: {syncText}
        </span>
      </p>
    );
  };

  return (
    <div style={{ marginBottom: "1rem", textAlign: "center" }}>
      {title && (
        <p
          style={{ margin: 0, fontWeight: "normal", fontSize: "2.25rem", ...titleStyle }}
          onClick={onTitleClick}
          onDoubleClick={onTitleDoubleClick}
        >
          {title}
        </p>
      )}
      {storeName && (
        <p style={{ fontSize: "1rem", color: "#666", marginTop: "-0.05rem" }}>
          {storeName}
        </p>
      )}
      {renderMode()}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2600}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <style>
        {`
          @keyframes storeTitleSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default StoreTitle;
